/*eslint-disable react-hooks/exhaustive-deps */
import { styled } from "@stitches/react";
import {
  Button,
  Modal,
  SingleSelect,
  MultiselectChangeEvent,
  StructuredOption,
  Toast,
} from "@/vendor/Forge/dist";
import { AuthStatus, Order } from "@/types/my-tasks";
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useAssignUser, useBucketReAssign } from "@/hooks/use-notes";
import { OrdersQueryKey } from "@/types/order-table";
import { useAppState } from "@/contexts/app-state";
import { capitalize } from "@/utils/helpers";
import AuthStatusForm from "../AuthStatusForm";
import { useUsers } from "@hooks/use-users";
import { ToastMessageType } from "@/types/order-table";

const SelectionFieldContainer = styled("div", {
  height: 65,
  width: "100%",
  backgroundColor: "#F3F3F3",
  marginBottom: "10px",
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  gap: 15,
});

const SelectedItemsWrapper = styled("div", {
  "& p": {
    fontWeight: "bold",
    fontSize: "16px",
    lineHeight: "24px",
    textAlign: "center",
    marginBottom: 0,
    marginLeft: "10px",
  },
});

const BulkActionIdentifierWrapper = styled("div", {
  "& p": {
    marginBottom: 0,
  },
});

export interface Props {
  checkedOrderIds: string[];
  deselectAllOrders(deselect: boolean): void;
  rowState: Order[];
  ordersQueryKey: OrdersQueryKey;
  setToastMessageData: (value: ToastMessageType) => void;
}

type currentPage = "" | "admin" | "my-tasks";

const BulkSelectionField = ({
  checkedOrderIds,
  deselectAllOrders,
  rowState,
  ordersQueryKey,
  setToastMessageData,
}: Props) => {
  const { assignUser } = useAssignUser({ ordersQueryKey });
  const { bucketReAssign } = useBucketReAssign({ ordersQueryKey });
  const { loggedInUser } = useAppState();
  const [currentPage, setCurrentPage] = useState<currentPage>("admin");
  const [bulkAuthStatus, setBulkAuthStatus] =
    useState<AuthStatus>("Not Started");
  const [bulkAssign, setBulkAssign] = useState<string>("");
  const [modal, setModal] = useState(false);
  const [bulkActionIdentifier, setBulkActionIdentifier] = useState<
    string | undefined
  >("");
  const [bulkActionWarningBanner, setBulkActionWarningBanner] =
    useState<boolean>();
  const { data: users = [] } = useUsers();
  const { pathname } = useLocation();

  useEffect(() => {
    if (bulkActionIdentifier == "Change Auth Status to Not Started") {
      setBulkAssign("Not Started");
    } else if (bulkActionIdentifier == "Change Auth Status to Not Required") {
      setBulkAssign("Not Required");
    } else {
      setBulkAssign("");
    }
  }, [bulkActionIdentifier]);

  useEffect(() => {
    if (pathname.includes("admin")) {
      setCurrentPage("admin");
    } else if (pathname.includes("my-tasks")) {
      setCurrentPage("my-tasks");
    } else {
      setCurrentPage("");
    }
  }, [pathname]);

  useEffect(() => {
    setBulkActionIdentifier("Change Auth Status to Not Required");
  }, []);

  useEffect(() => {
    if (bulkActionIdentifier == "Assign all to User") {
      setBulkActionWarningBanner(true);
    }
    if (bulkActionIdentifier == "Assign all to Bucket") {
      setBulkActionWarningBanner(true);
    }
  }, [bulkActionIdentifier]);

  const handleDeselect = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    deselectAllOrders(true);
  };

  const handleAuthStatusBulkChange = (newStatus: AuthStatus) => {
    setBulkAuthStatus(newStatus);
  };

  const handleBulkAssign = (bulkAssign: string) => {
    setBulkAssign(bulkAssign);
  };

  const handleOpenModal = () => {
    setModal(true);
  };

  const handleBulkUserAssignClick = (
    orderIds: string[],
    bulkAssign: string,
  ) => {
    assignUser({
      orderIds: orderIds,
      actionNote: `${capitalize(
        loggedInUser,
      )} assigned orders to ${bulkAssign}.`,
      changedField: "USER_RE_ASSIGN",
      changedValue: bulkAssign,
    });
  };
  const handleBulkBucketAssignClick = (
    orderIds: string[],
    newBucketBulkAssign: string,
  ) => {
    bucketReAssign({
      orderIds: orderIds,
      actionNote: `${capitalize(
        loggedInUser,
      )} assigned buckets to ${newBucketBulkAssign}.`,
      changedField: "BUCKET_RE_ASSIGN",
      changedValue: newBucketBulkAssign,
    });
  };

  return (
    <SelectionFieldContainer>
      <SelectedItemsWrapper>
        <p data-testid="selected">{checkedOrderIds.length} Selected</p>
      </SelectedItemsWrapper>
      <BulkActionIdentifierWrapper data-testid="bulk-action-wrapper">
        <p>
          {currentPage == "admin" ? (
            <></>
          ) : currentPage == "my-tasks" ? (
            "Auth Status:"
          ) : undefined}
        </p>
      </BulkActionIdentifierWrapper>
      {currentPage == "admin" ? (
        <SingleSelect
          name="bulkActionIdentifierSelect"
          data-testid="identifierSelect"
          options={[
            "Change Auth Status to Not Required",
            "Change Auth Status to Not Started",
            "Assign all to User",
            "Assign all to Bucket",
          ]}
          onChange={(e: MultiselectChangeEvent<StructuredOption, false>) =>
            setBulkActionIdentifier(e.target.value?.value)
          }
          value={
            bulkActionIdentifier == ""
              ? "-Select an Action-"
              : bulkActionIdentifier
          }
        />
      ) : undefined}
      {currentPage == "admin" &&
      bulkActionIdentifier == "Assign all to User" ? (
        <SingleSelect
          data-testid="selectField"
          options={rowState[0].allUsers?.map((a) => a.userName)}
          onChange={(e: MultiselectChangeEvent<StructuredOption, false>) => {
            handleBulkAssign(String(e.target.value?.value));
          }}
          defaultValue={null}
          value={bulkAssign == "" ? null : bulkAssign}
        />
      ) : currentPage == "admin" &&
        bulkActionIdentifier == "Assign all to Bucket" ? (
        <SingleSelect
          data-testid="selectField"
          options={users.filter((user) => user !== "All")}
          onChange={(e: MultiselectChangeEvent<StructuredOption, false>) => {
            handleBulkAssign(String(e.target.value?.value));
          }}
          defaultValue={null}
          value={bulkAssign == "" ? null : bulkAssign}
        />
      ) : currentPage == "my-tasks" ? (
        <SingleSelect
          data-testid="selectField"
          options={[
            { label: "Not Started", value: "Not Started" },
            { label: "Pending", value: "Pending", disabled: true },
            { label: "Not Required", value: "Not Required" },
            { label: "Obtained", value: "Obtained", disabled: true },
            { label: "Closed", value: "Closed", disabled: true },
          ]}
          onChange={(e: MultiselectChangeEvent<StructuredOption, false>) => {
            handleAuthStatusBulkChange(e.target.value?.value as AuthStatus);
          }}
        />
      ) : undefined}
      <Button
        variant="secondary"
        data-testid="updateButton"
        onClick={() => {
          handleOpenModal();
          setToastMessageData({
            changedType: bulkActionIdentifier ?? "",
            changedValue: bulkAssign,
            changedAmount: checkedOrderIds.length,
          });
        }}
      >
        {currentPage == "admin"
          ? "Update"
          : currentPage == "my-tasks"
          ? "Change Auth Status"
          : undefined}
      </Button>
      <Button
        variant="tertiary"
        data-testid="deselectButton"
        onClick={handleDeselect}
      >
        Deselect All
      </Button>
      {currentPage == "admin" &&
      bulkActionIdentifier == "Assign all to User" ? (
        <Modal
          data-testid="modal"
          headerText="Are you sure?"
          show={modal}
          onPrimaryClick={() => {
            handleBulkUserAssignClick(checkedOrderIds, bulkAssign);
          }}
          onHide={() => {
            setModal(false);
          }}
          onBackdropClick={() => {
            setModal(false);
          }}
        ></Modal>
      ) : currentPage == "admin" &&
        bulkActionIdentifier == "Assign all to Bucket" ? (
        <Modal
          data-testid="modal"
          headerText="Are you sure?"
          show={modal}
          onPrimaryClick={() => {
            handleBulkBucketAssignClick(checkedOrderIds, bulkAssign);
          }}
          onHide={() => {
            setModal(false);
          }}
          onBackdropClick={() => {
            setModal(false);
          }}
        ></Modal>
      ) : currentPage == "admin" &&
        bulkActionIdentifier == "Change Auth Status to Not Started" ? (
        <Modal
          data-testid="modal"
          show={modal}
          includeButtonRow={false}
          onHide={() => {
            setModal(false);
          }}
          onBackdropClick={() => {
            setModal(false);
          }}
          headerText={`Auth Status → ${bulkAssign}`}
        >
          <AuthStatusForm
            ordersQueryKey={ordersQueryKey}
            order={rowState[0]}
            documentIds={checkedOrderIds}
            newStatus="Not Started"
            onSubmit={() => setModal(false)}
            onCancelClick={() => setModal(false)}
          />
        </Modal>
      ) : currentPage == "admin" &&
        bulkActionIdentifier == "Change Auth Status to Not Required" ? (
        <Modal
          data-testid="modal"
          show={modal}
          includeButtonRow={false}
          onHide={() => {
            setModal(false);
          }}
          onBackdropClick={() => {
            setModal(false);
          }}
          headerText={`Auth Status → ${bulkAssign}`}
        >
          <AuthStatusForm
            ordersQueryKey={ordersQueryKey}
            order={rowState[0]}
            documentIds={checkedOrderIds}
            newStatus="Not Required"
            onSubmit={() => setModal(false)}
            onCancelClick={() => setModal(false)}
          />
        </Modal>
      ) : currentPage == "my-tasks" ? (
        <Modal
          data-testid="modal"
          show={modal}
          includeButtonRow={false}
          onHide={() => {
            setModal(false);
          }}
          onBackdropClick={() => {
            setModal(false);
          }}
          headerText={`Auth Status → ${bulkAuthStatus}`}
        >
          <AuthStatusForm
            ordersQueryKey={ordersQueryKey}
            order={rowState[0]}
            documentIds={checkedOrderIds}
            newStatus={bulkAuthStatus}
            onSubmit={() => setModal(false)}
            onCancelClick={() => setModal(false)}
          />
        </Modal>
      ) : (
        <></>
      )}
      {bulkActionWarningBanner &&
      bulkActionIdentifier == "Assign all to User" ? (
        <Toast
          alertType="attention"
          id="userAssignmentWarning"
          headerText="Please Select a User"
          onDismiss={() => setBulkActionWarningBanner(false)}
        >
          Please type or select a user to continue updates
        </Toast>
      ) : (
        <></>
      )}
      {bulkActionWarningBanner &&
      bulkActionIdentifier == "Assign all to Bucket" ? (
        <Toast
          alertType="attention"
          id="bucketAssignmentWarning"
          headerText="Please Select a Bucket"
          onDismiss={() => setBulkActionWarningBanner(false)}
        >
          Please type or select a bucket to continue updates
        </Toast>
      ) : (
        <></>
      )}
    </SelectionFieldContainer>
  );
};

export default BulkSelectionField;
